import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { WarehouseLabelApi } from 'api/warehouseLabels';
import { isValidCaseLabelCount } from 'components/receiving/components/AllocatedReceiving/PrintLabels/printLabelsUtils';
import { downloadBlob, printPdfBlob } from 'utils/fileDownload';

const PALLET_TAGS_FILENAME = 'pallet_tags.pdf';

/**
 * @param {Array<T>} items
 * @param {(item: T) => Promise<void>} processItem
 * @template T
 */
async function runSequentially(items, processItem) {
    await items.reduce(async (previous, item) => {
        await previous;
        await processItem(item);
    }, Promise.resolve());
}

/**
 * @param {import('api/warehouseLabels').WarehouseLabelApi} labelApi
 * @param {{ lotID: string | number, qty: number }} params
 */
async function fetchCaseLabelsOrThrow(labelApi, { lotID, qty }) {
    const blob = await labelApi.getCaseLabels({ lotID, qty });
    if (!blob) {
        throw new Error('Failed to fetch case labels');
    }
    return blob;
}

/**
 * @param {import('api/warehouseLabels').WarehouseLabelApi} labelApi
 * @param {{ palletID: string | number, qty: number }} params
 */
async function fetchPalletLabelsOrThrow(labelApi, { palletID, qty }) {
    const blob = await labelApi.getPalletLabels({ palletID, qty });
    if (!blob) {
        throw new Error('Failed to fetch pallet labels');
    }
    return blob;
}

/**
 * @param {import('api/warehouseLabels').WarehouseLabelApi} labelApi
 * @param {{ palletIDs: Array<string | number>, qty: number }} params
 */
async function fetchMultiplePalletLabelsOrThrow(labelApi, { palletIDs, qty }) {
    const blob = await labelApi.getMultiplePalletLabels(palletIDs, qty);
    if (!blob) {
        throw new Error('Failed to fetch pallet labels');
    }
    return blob;
}

/**
 * @param {Object} options
 * @param {{ pallets: Array<{ id: string, lots: Array<{ id: string, caseCount: number }> }> }} options.receivedPrintData
 * @param {(error: Error) => void} [options.onError]
 * @param {(error: Error) => void} [options.onDownloadError]
 */
export function useAllocatedLabelPrinting({ onDownloadError, onError, receivedPrintData }) {
    const labelApi = useMemo(() => new WarehouseLabelApi(), []);

    const printCaseTagsMutation = useMutation({
        mutationFn: async ({ lotId, qty }) => {
            const blob = await fetchCaseLabelsOrThrow(labelApi, { lotID: lotId, qty });
            await printPdfBlob(blob);
        },
        onError,
    });

    const printPalletTagsMutation = useMutation({
        mutationFn: async ({ palletId, qty }) => {
            const blob = await fetchPalletLabelsOrThrow(labelApi, { palletID: palletId, qty });
            await printPdfBlob(blob);
        },
        onError,
    });

    const printAllPalletTagsMutation = useMutation({
        mutationFn: async ({ tagsPerPallet }) => {
            const palletIDs = receivedPrintData.pallets.map((pallet) => pallet.id);
            const blob = await fetchMultiplePalletLabelsOrThrow(labelApi, { palletIDs, qty: tagsPerPallet });
            await printPdfBlob(blob);
        },
        onError,
    });

    const downloadAllLabelsMutation = useMutation({
        mutationFn: async ({ caseLabelQtyByLot, tagsPerPallet }) => {
            const palletIDs = receivedPrintData.pallets.map((pallet) => pallet.id);
            const palletBlob = await fetchMultiplePalletLabelsOrThrow(labelApi, { palletIDs, qty: tagsPerPallet });
            downloadBlob(palletBlob, PALLET_TAGS_FILENAME);

            const lotsToDownload = receivedPrintData.pallets
                .flatMap((pallet) => pallet.lots)
                .map((lot) => {
                    const stored = caseLabelQtyByLot.get(lot.id);
                    const qty = stored !== undefined ? stored : lot.caseCount;
                    return { lot, qty };
                })
                .filter(({ lot, qty }) => isValidCaseLabelCount(qty, lot.caseCount));

            await runSequentially(lotsToDownload, async ({ lot, qty }) => {
                const caseBlob = await fetchCaseLabelsOrThrow(labelApi, { lotID: lot.id, qty });
                downloadBlob(caseBlob, `case_tags_${lot.id}.pdf`);
            });
        },
        onError: onDownloadError ?? onError,
    });

    const runWithoutUnhandledRejection = useCallback((mutateAsync, variables) => {
        mutateAsync(variables).catch(() => {});
    }, []);

    const printCaseTags = useCallback(
        (params) => runWithoutUnhandledRejection(printCaseTagsMutation.mutateAsync, params),
        [printCaseTagsMutation.mutateAsync, runWithoutUnhandledRejection],
    );

    const printPalletTags = useCallback(
        (params) => runWithoutUnhandledRejection(printPalletTagsMutation.mutateAsync, params),
        [printPalletTagsMutation.mutateAsync, runWithoutUnhandledRejection],
    );

    const printAllPalletTags = useCallback(
        (params) => runWithoutUnhandledRejection(printAllPalletTagsMutation.mutateAsync, params),
        [printAllPalletTagsMutation.mutateAsync, runWithoutUnhandledRejection],
    );

    const downloadAllLabels = useCallback(
        (params) => runWithoutUnhandledRejection(downloadAllLabelsMutation.mutateAsync, params),
        [downloadAllLabelsMutation.mutateAsync, runWithoutUnhandledRejection],
    );

    const isPrinting =
        printCaseTagsMutation.isPending ||
        printPalletTagsMutation.isPending ||
        printAllPalletTagsMutation.isPending ||
        downloadAllLabelsMutation.isPending;

    return {
        downloadAllLabels,
        isPrinting,
        printAllPalletTags,
        printCaseTags,
        printPalletTags,
    };
}
